import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, X } from "lucide-react";
import { api } from "../lib/api";
import { LightArt } from "../components/LightArt";
import { Navigation } from "../components/Navigation";

const CATEGORIES = [
  { id: "all", label: "Alle" },
  { id: "spiritual", label: "Spirituelle Fotografie" },
  { id: "weather", label: "Wetterfotografie" },
  { id: "forest", label: "Waldfotografie" },
];

const CAT_LABEL = {
  spiritual: "Spirituell",
  weather: "Wetter",
  forest: "Wald",
};

export default function GaleriePage() {
  const [items, setItems] = useState(null);
  const [active, setActive] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    document.title = "Galerie · Die Lichtmalerin";
    window.scrollTo(0, 0);
    api
      .get("/gallery")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return null;
    if (active === "all") return items;
    return items.filter((it) => (it.category || "spiritual") === active);
  }, [items, active]);

  const counts = useMemo(() => {
    const c = { all: items?.length || 0, spiritual: 0, weather: 0, forest: 0 };
    (items || []).forEach((it) => {
      const cat = it.category || "spiritual";
      c[cat] = (c[cat] || 0) + 1;
    });
    return c;
  }, [items]);

  return (
    <div className="min-h-screen bg-[#090909]" data-testid="galerie-page">
      <Navigation />

      <section className="pt-28 md:pt-36 pb-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/"
            data-testid="galerie-back"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] font-sans font-light text-[#A1A1AA] hover:text-[#D4AF37] transition-colors group"
          >
            <ArrowLeft
              size={14}
              strokeWidth={1.4}
              className="group-hover:-translate-x-1 transition-transform"
            />
            Zurück
          </Link>

          <div className="mt-8 md:mt-10">
            <span className="gold-divider">Werkarchiv</span>
            <h1
              data-testid="galerie-heading"
              className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#E5E5E5] mt-8"
            >
              Die ganze <span className="italic gold-text">Sammlung</span>
            </h1>
            <p className="mt-6 max-w-xl font-sans font-light text-base text-[#E5E5E5]/70 leading-relaxed">
              Werke aus Jahren voller Nächte, Wetter und stiller Wälder.
              Wählen Sie eine Rubrik oder betrachten Sie alle Arbeiten.
            </p>
          </div>

          {/* Category tabs */}
          <div
            className="mt-12 flex flex-wrap gap-2 md:gap-3"
            data-testid="galerie-tabs"
          >
            {CATEGORIES.map((c) => {
              const isActive = active === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c.id)}
                  data-testid={`galerie-tab-${c.id}`}
                  className={`px-5 py-2 text-[11px] uppercase tracking-[0.3em] font-sans font-light border transition-all ${
                    isActive
                      ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-[#D4AF37]/15 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/40"
                  }`}
                >
                  {c.label}
                  <span className="ml-2 text-[9px] text-[#A1A1AA]/60">
                    {counts[c.id] || 0}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Grid */}
          <div className="mt-12">
            {items === null ? (
              <p className="text-[#A1A1AA] font-sans" data-testid="galerie-loading">
                Lade…
              </p>
            ) : filtered.length === 0 ? (
              <div
                className="py-20 text-center"
                data-testid="galerie-empty"
              >
                <p className="font-serif italic text-2xl text-[#E5E5E5]/70">
                  {active === "all"
                    ? "Das Archiv wird bald mit Werken gefüllt."
                    : "In dieser Rubrik gibt es noch keine Werke."}
                </p>
                <p className="mt-4 text-xs uppercase tracking-[0.3em] text-[#A1A1AA]">
                  Nicole ergänzt hier laufend neue Arbeiten.
                </p>
              </div>
            ) : (
              <div
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
                data-testid="galerie-grid"
              >
                {filtered.map((g, i) => (
                  <motion.button
                    key={g.id}
                    data-testid={`galerie-item-${g.id}`}
                    onClick={() => setLightbox(g)}
                    initial={{ opacity: 0, filter: "blur(20px) brightness(0.2)" }}
                    whileInView={{ opacity: 1, filter: "blur(0px) brightness(1)" }}
                    transition={{ duration: 1, delay: (i % 8) * 0.05 }}
                    viewport={{ once: true, margin: "-40px" }}
                    className="group relative aspect-[4/5] overflow-hidden border border-[#D4AF37]/12 bg-[#0b0b0b]"
                  >
                    <img
                      src={g.image_base64}
                      alt={g.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                    />
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                      style={{
                        boxShadow: "inset 0 0 60px rgba(212,175,55,0.35)",
                      }}
                    />
                    <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-black/90 to-transparent text-left">
                      <p className="font-serif italic text-sm md:text-base text-[#E5E5E5]">
                        {g.title}
                      </p>
                      <p className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]/70 mt-1">
                        {CAT_LABEL[g.category] || "Spirituell"}
                        {g.year && ` · ${g.year}`}
                      </p>
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>

          {/* Placeholder note when empty */}
          {items !== null && items.length === 0 && (
            <div
              className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 opacity-40"
              data-testid="galerie-placeholder-grid"
            >
              {[1, 2, 3, 4, 5, 6, 3, 2].map((v, i) => (
                <div
                  key={i}
                  className="aspect-[4/5] border border-[#D4AF37]/10 overflow-hidden"
                >
                  <LightArt variant={((v + i) % 6) + 1} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="galerie-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 text-[#E5E5E5]/70 hover:text-[#D4AF37]"
              data-testid="galerie-lightbox-close"
              aria-label="Schließen"
            >
              <X size={28} strokeWidth={1.2} />
            </button>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="max-w-5xl w-full max-h-[85vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightbox.image_base64}
                alt={lightbox.title}
                className="max-h-[70vh] w-auto object-contain border border-[#D4AF37]/20"
              />
              <div className="mt-6 text-center">
                <p className="font-serif italic text-2xl text-[#E5E5E5]">
                  {lightbox.title}
                </p>
                <p className="mt-2 text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]/70">
                  {CAT_LABEL[lightbox.category] || "Spirituell"}
                  {lightbox.year && ` · ${lightbox.year}`}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
